/**
 * api/dashboard.js — queries da tela de Dashboard.
 *
 * Endpoints:
 *   GET /dashboard/projects?limit=20      — projetos recentes do tenant (enriquecidos)
 *   GET /dashboard/kpis[?mes=YYYY-MM]     — KPIs do período (fluxos, tokens, custo,
 *                                           série mensal de custo, variações e as
 *                                           contagens do Mission Control)
 *   GET|PUT /dashboard/budget             — orçamento mensal do tenant
 *
 * SEM DADOS INVENTADOS: não existe mais fallback de "placeholder" (antes, com a API
 * fora, a tela mostrava projetos e valores fictícios). Falha vira estado de erro
 * com "Tentar novamente"; ausência de valor vira "—".
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

/** Chave do cache da lista de projetos recentes (compartilhada com a busca global). */
export const RECENT_PROJECTS_KEY = ['dashboard', 'projects'];

/** Busca os projetos recentes (array). Lança o erro da API — sem fallback. */
export async function fetchRecentProjects(api) {
  const data = await api.get('/dashboard/projects?limit=20');
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

/**
 * useRecentProjects — lista os projetos mais recentes do tenant.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useRecentProjects() {
  const api = useApi();
  return useQuery({
    queryKey: RECENT_PROJECTS_KEY,
    queryFn: () => fetchRecentProjects(api),
    staleTime: 10_000,
    refetchInterval: 15_000,      // atualiza a tabela sozinho
    refetchOnWindowFocus: true,
  });
}

/**
 * useDashboardKpis — KPIs agregados do período.
 *
 * @param {{ mes?: string|null }} [opts] mês de referência `YYYY-MM` (filtro de
 *   período do painel de budget); sem ele, o mês corrente no servidor.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useDashboardKpis({ mes = null } = {}) {
  const api = useApi();
  const qs = mes ? `?mes=${encodeURIComponent(mes)}` : '';
  return useQuery({
    queryKey: mes ? ['dashboard', 'kpis', mes] : ['dashboard', 'kpis'],
    queryFn: () => api.get(`/dashboard/kpis${qs}`),
    staleTime: 5_000,
    refetchInterval: 10_000,          // busca gates/KPIs sozinho, sem refresh manual
    refetchIntervalInBackground: true, // continua procurando mesmo com a aba em 2º plano
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,  // troca de período sem piscar (mantém o último)
  });
}

// ─── Budget (orçamento mensal do tenant) ──────────────────────────────────────

/**
 * useBudget — orçamento mensal configurado ({ monthlyAmount, currency }).
 */
export function useBudget() {
  const api = useApi();
  return useQuery({
    queryKey: ['dashboard', 'budget'],
    queryFn: () => api.get('/dashboard/budget'),
    staleTime: 60_000,
  });
}

/**
 * useSetBudget — define o orçamento mensal (PUT). Requer budget_costs:create_edit.
 * Ao salvar, invalida o budget e os KPIs (o % consumido depende do orçamento).
 */
export function useSetBudget() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put('/dashboard/budget', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'budget'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'kpis'] });
    },
  });
}
